import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  BadgeCheck, ShieldCheck, MapPinCheck, Wind, Truck, Fingerprint,
  FlaskConical, Gauge, ScrollText, ArrowRight, Zap, Scale,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { usePageMeta } from "../lib/seo";

const trustItems = [
  { icon: BadgeCheck, label: "DOT Certified Collector" },
  { icon: ShieldCheck, label: "SAMHSA Compliant" },
  { icon: MapPinCheck, label: "DC Licensed & Insured" },
  { icon: Wind, label: "BAT Certified" },
  { icon: Truck, label: "Mobile Testing Available" },
  { icon: Fingerprint, label: "Fingerprinting Certified" },
];

const services = [
  {
    icon: FlaskConical,
    title: "Drug Testing",
    body: "Pre-employment, random, post-accident, and federal DOT testing. Urine and oral fluid collection with fast turnaround and full chain of custody documentation.",
  },
  {
    icon: Gauge,
    title: "Alcohol Testing",
    body: "BAT-certified breath alcohol testing for DOT and non-DOT employers. Available for post-accident and reasonable suspicion situations.",
  },
  {
    icon: Fingerprint,
    title: "Fingerprinting",
    body: "Ink card and live scan fingerprinting for employment screening, professional licensing, and federal credentialing.",
  },
  {
    icon: ScrollText,
    title: "Compliance Consulting",
    body: "DC and Maryland employer drug testing law guidance, policy development, and supervisor reasonable suspicion training.",
  },
];

const why = [
  {
    num: "01",
    icon: Zap,
    title: "Fast & Reliable",
    body: "Results you can count on with rapid turnaround times and responsive service. Your hiring decisions and compliance deadlines can't wait.",
  },
  {
    num: "02",
    icon: Truck,
    title: "Mobile Capable",
    body: "We come to you. On-site mobile testing for post-accident, random, and pre-employment collections means minimal disruption to your operations.",
  },
  {
    num: "03",
    icon: Scale,
    title: "DC & Maryland Compliance Experts",
    body: "Navigating cannabis employment protections and federal contractor requirements in DC and Maryland requires more than a test. It requires a partner who knows the law.",
  },
];

export default function Home() {
  usePageMeta(
    "VeraScreen Solutions | Drug Testing, Alcohol Screening & Fingerprinting — DC & Maryland",
    "Professional drug testing, alcohol screening, and fingerprinting services for DC and Maryland employers. DOT certified. Mobile capable. Compliance ready."
  );

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yRing = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-[#1A3A6B]">
        <motion.div style={{ y: yBg }} className="hero-grid absolute inset-0" />
        <div className="grain-overlay" />
        <motion.div
          style={{ y: yRing }}
          className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full border border-white/10 md:-right-20"
        >
          <div className="absolute inset-10 rounded-full border border-[#B8962E]/25" />
          <div className="absolute inset-24 rounded-full border border-[#0E7B6B]/40" />
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-5 pb-28 pt-24 md:pb-36 md:pt-32 lg:px-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-6 text-xs font-bold uppercase tracking-[0.3em] text-[#B8962E]"
          >
            Washington DC · Maryland · Northern Virginia
          </motion.p>

          <h1 className="font-display text-5xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.15}>Verified Results.</MaskedLine>
            <MaskedLine delay={0.3} className="text-[#4FB3A5]">Confident Decisions.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg"
          >
            Professional drug testing, alcohol screening, and fingerprinting services for DC and
            Maryland employers. DOT certified. Mobile capable. Compliance ready.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-10"
          >
            <Link to="/request-a-screening" data-testid="hero-request-screening-btn" className="btn-gold">
              Request a Screening <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <section className="border-b border-gray-100 bg-white py-6" data-testid="trust-bar">
        <Marquee speed={35} gradient gradientColor="white" gradientWidth={80} autoFill pauseOnHover>
          {trustItems.map((t) => (
            <span key={t.label} className="mx-8 flex items-center gap-2.5 text-sm font-semibold text-[#1A3A6B]">
              <t.icon size={20} className="text-[#0E7B6B]" strokeWidth={1.5} />
              {t.label}
            </span>
          ))}
        </Marquee>
      </section>

      {/* SERVICES SNAPSHOT */}
      <section className="bg-[#F5F5F5] py-24" data-testid="services-snapshot">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#0E7B6B]">What We Do</p>
            <h2 className="font-display mt-3 max-w-xl text-3xl font-extrabold tracking-tight text-[#1A3A6B] md:text-4xl">
              Screening services built for compliance
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div
                  data-testid={`service-tile-${i}`}
                  className="group flex h-full flex-col border border-gray-200 bg-white p-8 transition-transform duration-300 hover:-translate-y-1.5"
                >
                  <s.icon size={32} className="text-[#0E7B6B]" strokeWidth={1.5} />
                  <h3 className="font-display mt-6 text-xl font-bold text-[#1A3A6B]">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[#333333]/80">{s.body}</p>
                  <Link
                    to="/services"
                    data-testid={`service-tile-learn-more-${i}`}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#B8962E] transition-colors duration-200 hover:text-[#9E7F22]"
                  >
                    Learn More <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY VERASCREEN — numbered manifesto */}
      <section className="bg-white py-24 md:py-32" data-testid="why-verascreen">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#0E7B6B]">Why VeraScreen</p>
            <h2 className="font-display mt-3 max-w-2xl text-3xl font-extrabold tracking-tight text-[#1A3A6B] md:text-4xl">
              A screening partner, not just a collection site
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-12 md:grid-cols-3">
            {why.map((w, i) => (
              <Reveal key={w.num} delay={i * 0.1}>
                <div className="relative border-t-2 border-[#1A3A6B] pt-8">
                  <span className="font-display absolute -top-7 right-0 text-6xl font-extrabold text-[#F5F5F5] md:text-7xl">
                    {w.num}
                  </span>
                  <w.icon size={28} className="text-[#0E7B6B]" strokeWidth={1.5} />
                  <h3 className="font-display mt-5 text-xl font-bold text-[#1A3A6B]">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#333333]/80">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative overflow-hidden bg-[#1A3A6B] py-20 md:py-24" data-testid="cta-banner">
        <div className="grain-overlay" />
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-white md:text-4xl">
              Ready to build a compliant, drug-free workplace?
            </h2>
            <p className="mt-4 text-base text-white/70 md:text-lg">VeraScreen Solutions makes it simple.</p>
            <Link to="/contact" data-testid="cta-get-started-btn" className="btn-gold mt-8">
              Get Started Today <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
